import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

type Point = { x: number; y: number };

const PLOT = { L: 0.5, R: 99.5, T: 2, B: 86 } as const;

export type ChartInterpolation = 'linear' | 'step';

export interface SimpleLineChartProps {
  values: number[];
  labels?: string[];
  stroke?: string;
  height?: number;
  emptyLabel?: string;
  showGrid?: boolean;
  lineOnly?: boolean;
  formatValue?: (v: number) => string;
  seriesName?: string;
  interpolation?: ChartInterpolation;
  showRightAxis?: boolean;
}

function xAxisMode(labels: string[] | undefined): 'clock' | 'day' {
  if (!labels || labels.length < 2) return 'clock';
  try {
    const a = new Date(labels[0]).getTime();
    const b = new Date(labels[labels.length - 1]).getTime();
    const spanH = (b - a) / (3600 * 1000);
    return spanH > 36 ? 'day' : 'clock';
  } catch {
    return 'clock';
  }
}

function formatAxisTick(iso: string, mode: 'clock' | 'day'): string {
  try {
    const d = new Date(iso);
    if (mode === 'day') return format(d, 'd MMM yyyy', { locale: tr });
    return format(d, 'd MMM HH:mm', { locale: tr });
  } catch {
    return iso;
  }
}

function formatTooltipTime(iso: string): string {
  try {
    return format(new Date(iso), 'd MMM yyyy, HH:mm:ss', { locale: tr });
  } catch {
    return iso;
  }
}

function compactAxisNumber(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  if (Number.isInteger(v)) return v.toLocaleString('tr-TR');
  return v.toFixed(1);
}

function buildLinearPath(pts: Point[]): string {
  if (pts.length === 0) return '';
  return pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
}

function buildSteppedPath(pts: Point[]): string {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` L ${pts[i].x} ${pts[i - 1].y} L ${pts[i].x} ${pts[i].y}`;
  }
  return d;
}

function buildAreaPath(linePath: string, pts: Point[], bottom: number): string {
  if (pts.length === 0) return '';
  const last = pts[pts.length - 1];
  const first = pts[0];
  return `${linePath} L ${last.x} ${bottom} L ${first.x} ${bottom} Z`;
}

function pickIndexFromClientX(clientX: number, el: HTMLElement, n: number): number | null {
  if (n <= 0) return null;
  const rect = el.getBoundingClientRect();
  const t = (clientX - rect.left) / rect.width;
  const pL = PLOT.L / 100;
  const pR = PLOT.R / 100;
  const u = (t - pL) / (pR - pL);
  const clamped = Math.max(0, Math.min(1, u));
  return Math.round(clamped * (n - 1));
}

function clampTooltipLeftPct(hx: number): number {
  return Math.min(90, Math.max(10, hx));
}

export const SimpleLineChart: React.FC<SimpleLineChartProps> = ({
  values,
  labels,
  stroke = '#5794F2',
  height = 300,
  emptyLabel = 'Veri yok',
  showGrid = true,
  lineOnly = false,
  formatValue = (v) => String(v),
  seriesName = 'Seri',
  interpolation = 'linear',
  showRightAxis = true,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const gradId = useId().replace(/:/g, '');
  const areaGradId = `g-${gradId}`;
  const lineGradId = `l-${gradId}`;

  const { linePath, areaPath, hasData, pts, yTicks, plot } = useMemo(() => {
    if (!values.length) {
      return {
        linePath: '',
        areaPath: '',
        hasData: false,
        pts: [] as Point[],
        maxV: 0,
        minV: 0,
        yTicks: [] as number[],
        plot: PLOT,
      };
    }
    const rawMax = Math.max(...values);
    const rawMin = Math.min(...values);
    const span = Math.max(rawMax - rawMin, 1e-9);
    const pad = span * 0.08;
    const maxV = rawMax === rawMin && rawMax === 0 ? 1 : rawMax + pad;
    const minV = Math.max(0, rawMin - pad);
    const range = Math.max(maxV - minV, 1e-6);

    const yTicks = Array.from({ length: 5 }, (_, i) => maxV - (i / 4) * (maxV - minV));

    const innerW = PLOT.R - PLOT.L;
    const innerH = PLOT.B - PLOT.T;
    const pts: Point[] = values.map((v, i) => {
      const x = PLOT.L + (values.length <= 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
      const norm = (v - minV) / range;
      const y = PLOT.B - norm * innerH;
      return { x, y };
    });

    const linePath =
      interpolation === 'step' ? buildSteppedPath(pts) : buildLinearPath(pts);
    const areaPath = buildAreaPath(linePath, pts, PLOT.B);

    return { linePath, areaPath, hasData: true, pts, maxV, minV, yTicks, plot: PLOT };
  }, [values, interpolation]);

  const pickIndex = useCallback(
    (clientX: number) => {
      const el = wrapRef.current;
      if (!el || !values.length) return null;
      return pickIndexFromClientX(clientX, el, values.length);
    },
    [values.length]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const idx = pickIndex(e.clientX);
      if (idx !== null) setHoverIdx(idx);
    },
    [pickIndex]
  );

  const onPointerLeave = useCallback(() => {
    setHoverIdx(null);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const idx = pickIndex(e.clientX);
      if (idx !== null) setHoverIdx(idx);
    },
    [pickIndex]
  );

  const axisLabels = useMemo(() => {
    if (!labels?.length) return { ticks: [] as { label: string; title: string }[] };
    const mode = xAxisMode(labels);
    const n = labels.length;
    const want = Math.min(6, Math.max(2, Math.ceil(n / 12)));
    const indices: number[] = [];
    if (want === 2) {
      indices.push(0, n - 1);
    } else {
      for (let i = 0; i < want; i++) {
        indices.push(Math.round((i / (want - 1)) * (n - 1)));
      }
    }
    const uniq = Array.from(new Set(indices)).sort((a, b) => a - b);
    return {
      ticks: uniq.map((i) => ({
        label: formatAxisTick(labels[i], mode),
        title: formatTooltipTime(labels[i]),
      })),
    };
  }, [labels]);

  if (!hasData) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-dashed border-[#2d3239] bg-[#111217] text-sm text-[#7d8696]"
        style={{ minHeight: height }}
      >
        {emptyLabel}
      </div>
    );
  }

  const gridLines = showGrid
    ? [0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = plot.T + (plot.B - plot.T) * t;
        return (
          <line
            key={t}
            x1={plot.L}
            y1={y}
            x2={plot.R}
            y2={y}
            stroke="rgba(204,204,220,0.08)"
            strokeWidth="0.35"
          />
        );
      })
    : null;

  const showTooltip = hoverIdx !== null && values.length > 0;
  const hv = hoverIdx !== null ? values[hoverIdx] : 0;
  const hx = hoverIdx !== null && pts[hoverIdx] ? pts[hoverIdx].x : 0;
  const hy = hoverIdx !== null && pts[hoverIdx] ? pts[hoverIdx].y : 0;
  const tooltipLeft = clampTooltipLeftPct(hx);

  const strokeRgb = stroke;

  return (
    <div className="w-full overflow-hidden rounded-md border border-[#2d3239] bg-[#111217] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.03)]">
      <div className="flex min-h-0 gap-0">
        <div className="flex w-[52px] shrink-0 flex-col justify-between border-r border-[#2d3239] bg-[#0d0f14] py-2 pl-2 pr-1 text-right font-mono text-[10px] leading-none text-[#9d9fa8] tabular-nums [font-feature-settings:'tnum']">
          {yTicks.map((tv, i) => (
            <span key={i}>{compactAxisNumber(tv)}</span>
          ))}
        </div>
        <div
          ref={wrapRef}
          className="relative min-h-0 min-w-0 flex-1 touch-none cursor-crosshair"
          style={{ height }}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
          onPointerDown={onPointerDown}
        >
          <svg
            viewBox="0 0 100 100"
            className="h-full w-full select-none"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id={areaGradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokeRgb} stopOpacity={lineOnly ? 0 : 0.35} />
                <stop offset="100%" stopColor={strokeRgb} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={lineGradId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={strokeRgb} stopOpacity={0.85} />
                <stop offset="100%" stopColor={strokeRgb} stopOpacity={1} />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill="transparent" />
            {gridLines}
            {!lineOnly && <path d={areaPath} fill={`url(#${areaGradId})`} stroke="none" />}
            {hoverIdx !== null && pts[hoverIdx] && (
              <>
                <line
                  x1={hx}
                  x2={hx}
                  y1={plot.T}
                  y2={plot.B}
                  stroke="rgba(204,204,220,0.28)"
                  strokeWidth="0.5"
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1={plot.L}
                  x2={plot.R}
                  y1={hy}
                  y2={hy}
                  stroke="rgba(204,204,220,0.12)"
                  strokeWidth="0.35"
                  strokeDasharray="2 3"
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
            <path
              d={linePath}
              fill="none"
              stroke={`url(#${lineGradId})`}
              strokeWidth="2"
              strokeLinecap={interpolation === 'step' ? 'square' : 'round'}
              strokeLinejoin={interpolation === 'step' ? 'miter' : 'round'}
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {hoverIdx !== null && pts[hoverIdx] && (
            <div
              className="pointer-events-none absolute z-[15] h-2 w-2 rounded-full border-[2.5px] bg-[#111217] shadow-[0_0_0_4px_rgba(0,0,0,0.35)]"
              style={{
                left: `${hx}%`,
                top: `${hy}%`,
                transform: 'translate(-50%, -50%)',
                borderColor: strokeRgb,
              }}
            />
          )}

          {showTooltip && (
            <div
              className="pointer-events-none absolute z-20 min-w-[160px] max-w-[min(92vw,280px)] rounded border border-[#3d4048] bg-[#1e2026] px-3 py-2.5 text-left shadow-[0_4px_16px_rgba(0,0,0,0.45)]"
              style={{
                left: `${tooltipLeft}%`,
                bottom: 10,
                top: 'auto',
                transform: 'translateX(-50%)',
              }}
            >
              <div className="mb-1.5 flex items-center gap-2 border-b border-[#2d3239] pb-1.5">
                <span
                  className="h-2 w-0.5 shrink-0 rounded-full"
                  style={{ backgroundColor: strokeRgb }}
                />
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9d9fa8]">
                  {seriesName}
                </span>
              </div>
              <div className="font-mono text-lg font-semibold tabular-nums leading-none text-[#f4f5f8]">
                {formatValue(hv)}
              </div>
              {hoverIdx !== null && labels?.[hoverIdx] && (
                <div className="mt-2 font-mono text-[11px] leading-snug text-[#c8cdd8]">
                  {formatTooltipTime(labels[hoverIdx])}
                </div>
              )}
              {hoverIdx !== null && (
                <div className="mt-2 border-t border-[#2d3239] pt-2 font-mono text-[10px] text-[#6b7280]">
                  {hoverIdx + 1} / {values.length} örnek
                </div>
              )}
            </div>
          )}
        </div>
        {showRightAxis && (
          <div className="flex w-[52px] shrink-0 flex-col justify-between border-l border-[#2d3239] bg-[#0d0f14] py-2 pl-1 pr-2 text-left font-mono text-[10px] leading-none text-[#9d9fa8] tabular-nums [font-feature-settings:'tnum']">
            {yTicks.map((tv, i) => (
              <span key={i}>{compactAxisNumber(tv)}</span>
            ))}
          </div>
        )}
      </div>

      {labels && labels.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-[#2d3239] bg-[#0d0f14] px-3 py-2 font-mono text-[10px] text-[#c8cdd8]">
          {axisLabels.ticks.map((t, i) => (
            <span key={i} className="min-w-0 shrink tabular-nums text-[#9d9fa8]" title={t.title}>
              {t.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
